import { useEffect, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { History, Save, Users, ChevronRight, Check } from "lucide-react";
import { toast } from "sonner";

const PHASES = [
  { value: "site_visit", label: "Site Visit" },
  { value: "committee",  label: "Committee" },
  { value: "appeals",    label: "Appeals" },
  { value: "renewal",    label: "Renewal" },
];

const SCORE_OPTIONS = [
  { value: "meets",            label: "Meets Standard",            tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { value: "partially_meets",  label: "Partially Meets Standard",  tone: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "does_not_meet",    label: "Does Not Meet Standard",    tone: "bg-red-100 text-red-700 border-red-200" },
];

/**
 * 8-category reviewer scoring form embedded in school-application detail.
 * Backend at /api/reviewer-scores. History retention is automatic — every save
 * creates a new version.
 */
export default function ReviewerScoringPanel({ applicationId }) {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [phase, setPhase] = useState("site_visit");
  const [showHistory, setShowHistory] = useState(false);
  const [data, setData] = useState({ scores: [], by_category_avg: {}, overall_score: null, reviewer_count: 0 });
  const [drafts, setDrafts] = useState({}); // {category: {score, reviewer_notes}}
  const [busy, setBusy] = useState({}); // {category: true}
  const [loading, setLoading] = useState(true);

  // Load 8 standards categories + current scores
  const reload = async () => {
    setLoading(true);
    try {
      const [cats, scores] = await Promise.all([
        api.get("/standards/categories"),
        api.get("/reviewer-scores", {
          params: { school_application_id: applicationId,
                    scoring_phase: phase,
                    include_history: showHistory ? "true" : "false" },
        }),
      ]);
      setCategories(cats.data);
      setData(scores.data);
      // Seed editable drafts with caller's most recent score per category
      const mine = scores.data.scores.filter(
        (s) => s.is_current && s.reviewer_id === user?.id,
      );
      const next = {};
      mine.forEach((s) => {
        next[s.standards_category] = { score: s.score, reviewer_notes: s.reviewer_notes || "" };
      });
      setDrafts(next);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [applicationId, phase, showHistory]);

  const updateDraft = (cat, patch) => {
    setDrafts((d) => ({ ...d, [cat]: { ...(d[cat] || {}), ...patch } }));
  };

  const save = async (catId) => {
    const draft = drafts[catId];
    if (!draft?.score) {
      toast.error("Pick a score before saving");
      return;
    }
    setBusy((b) => ({ ...b, [catId]: true }));
    try {
      await api.post("/reviewer-scores", {
        school_application_id: applicationId,
        standards_category: catId,
        score: draft.score,
        reviewer_notes: draft.reviewer_notes || "",
        scoring_phase: phase,
      });
      toast.success("Score saved");
      reload();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy((b) => ({ ...b, [catId]: false }));
    }
  };

  return (
    <div className="space-y-5" data-testid="reviewer-scoring-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-slate-50 border border-slate-200 rounded-md">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <div className="text-xs text-slate-600">
            <strong className="text-primary">{data.reviewer_count}</strong> reviewer(s) ·{" "}
            <strong className="text-primary">
              {data.overall_score != null ? `${data.overall_score}% overall` : "Not yet scored"}
            </strong>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={phase} onValueChange={setPhase}>
            <SelectTrigger className="w-[170px] h-9" data-testid="phase-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PHASES.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant={showHistory ? "default" : "outline"}
            size="sm"
            onClick={() => setShowHistory((v) => !v)}
            data-testid="toggle-history"
          >
            <History className="h-3.5 w-3.5 mr-1" /> {showHistory ? "History on" : "Show history"}
          </Button>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading scores…</div>}

      <div className="space-y-3">
        {categories.map((cat) => {
          const current = data.scores.filter((s) => s.standards_category === cat.id && s.is_current);
          const allVersions = data.scores.filter((s) => s.standards_category === cat.id)
                                         .sort((a, b) => b.version - a.version);
          const draft = drafts[cat.id] || {};
          const avg = data.by_category_avg?.[cat.id];

          return (
            <Card key={cat.id} className="p-5 border-slate-200" data-testid={`cat-${cat.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-heading text-sm font-bold text-primary">{cat.label}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed mt-0.5">{cat.description}</p>
                </div>
                {avg != null && (
                  <Badge variant="outline" className="bg-slate-50 text-slate-700 text-[11px]">
                    avg {avg}%
                  </Badge>
                )}
              </div>

              {/* Existing reviewer scores (multi-reviewer) */}
              {current.length > 0 && (
                <div className="mb-3 space-y-1.5 p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
                    Current scores
                  </div>
                  {current.map((s) => (
                    <div key={s.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`current-score-${s.id}`}>
                      <Badge className={`border ${SCORE_OPTIONS.find((o) => o.value === s.score)?.tone}`}>
                        {s.score_label}
                      </Badge>
                      <span className="font-mono-data text-[11px] text-slate-500">v{s.version}</span>
                      <span className="text-slate-600">· {s.reviewer_email}</span>
                      <span className="text-slate-400 text-[11px]">
                        · {new Date(s.updated_at).toLocaleString()}
                      </span>
                      {s.reviewer_notes && (
                        <span className="basis-full text-slate-600 italic text-[11px]">"{s.reviewer_notes}"</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* History timeline when toggled */}
              {showHistory && allVersions.length > 0 && (
                <details className="mb-3" data-testid={`history-${cat.id}`}>
                  <summary className="cursor-pointer text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 hover:text-primary">
                    Full history ({allVersions.length})
                  </summary>
                  <div className="mt-2 space-y-1">
                    {allVersions.map((s) => (
                      <div key={s.id} className="flex items-center gap-2 text-[11px] py-1 border-b border-slate-100">
                        <span className="font-mono-data w-8">v{s.version}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {s.score_label}
                        </Badge>
                        <span className="text-slate-500 text-[10px]">{s.reviewer_email}</span>
                        <span className="text-slate-400 text-[10px] ml-auto">
                          {new Date(s.created_at).toLocaleDateString()}
                          {s.is_current && <Check className="h-3 w-3 inline ml-1 text-emerald-600" />}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Your score editor */}
              <div className="grid sm:grid-cols-3 gap-2 mb-2">
                {SCORE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateDraft(cat.id, { score: opt.value })}
                    data-testid={`score-${cat.id}-${opt.value}`}
                    className={`text-left px-3 py-2 rounded-md border transition ${
                      draft.score === opt.value
                        ? opt.tone + " ring-2 ring-primary/30 border-primary/40"
                        : "border-slate-200 hover:border-slate-300 text-slate-700 bg-white"
                    }`}
                  >
                    <span className="text-xs font-medium">{opt.label}</span>
                  </button>
                ))}
              </div>
              <Textarea
                rows={2}
                placeholder="Reviewer notes (visible to admins; preserved across versions)"
                data-testid={`notes-${cat.id}`}
                value={draft.reviewer_notes || ""}
                onChange={(e) => updateDraft(cat.id, { reviewer_notes: e.target.value })}
                className="text-xs"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  onClick={() => save(cat.id)}
                  disabled={!draft.score || busy[cat.id]}
                  className="bg-primary text-white hover:bg-[#113A64]"
                  data-testid={`save-${cat.id}`}
                >
                  <Save className="h-3.5 w-3.5 mr-1" />
                  {busy[cat.id] ? "Saving…" : "Save score"} <ChevronRight className="h-3 w-3 ml-0.5" />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
